import { diaryByIdQueryOptions } from "@/features/diaries/api";
import { redirectIfConsentRequired } from "@/features/consent/utils/redirectIfConsentRequired";
import { MissingSideForm } from "@/features/diaries/components/MissingSideForm";
import { PatientSetupPage } from "@/features/diaries/components/PatientSetupPage";
import type { MissingSideItem } from "@/features/diaries/utils/buildMissingSideItems";
import { buildMissingSideItems } from "@/features/diaries/utils/buildMissingSideItems";
import { getSideSetupCopyParts } from "@/features/diaries/utils/formatSideSetupCopy";
import { getPostSetupTarget } from "@/features/diaries/utils/postSetupNavigation";
import {
    patientNeedsHospitalNumberSetup,
    patientNeedsReferenceDateSetup,
} from "@/features/diaries/utils/missingDetailsBuilders";
import { useDiaryCopy } from "@/features/i18n/diaryCopy";
import { useSetupCopy } from "@/features/i18n/setupCopy";
import { hooks as h } from "@/hooks";
import type { StatefulDiaryT } from "@/types/database/diaries";
import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { Fragment } from "react";
import { z } from "zod";
import { useAuth } from "@/utils/auth";

const sideSetupSearchSchema = z.object({
    i: z.coerce.number().int().min(0).optional(),
});

const nameEmphasisClass = "font-semibold text-foreground underline decoration-foreground/50 underline-offset-2";

function SideSetupHighlightedNames({
    names,
    nameListSeparator,
}: {
    names: string[];
    nameListSeparator: (index: number, total: number) => string;
}) {
    if (names.length === 0) return null;
    if (names.length === 1) {
        return <strong className={nameEmphasisClass}>{names[0]}</strong>;
    }
    return (
        <>
            {names.map((n, i) => (
                <Fragment key={`${n}-${i}`}>
                    {nameListSeparator(i, names.length)}
                    <strong className={nameEmphasisClass}>{n}</strong>
                </Fragment>
            ))}
        </>
    );
}

const SetupSidePage = () => {
    const { diaryId } = Route.useParams();
    const diaryCopy = useDiaryCopy();
    const setupCopy = useSetupCopy();
    const auth = useAuth();
    const navigate = useNavigate({ from: Route.fullPath });
    const router = useRouter();
    const search = Route.useSearch();
    const { missingSideItems } = Route.useLoaderData() as {
        missingSideItems: MissingSideItem[];
        diary: StatefulDiaryT;
    };
    const { leadIn, names, closing, bothSidesNote } = getSideSetupCopyParts(missingSideItems, setupCopy);

    const perItemStepIndex = typeof search.i === "number" ? search.i : undefined;

    const navigateSearch = (next: { i?: number }) => {
        void navigate({
            to: "/diaries/$diaryId/setup/side",
            params: { diaryId },
            search: (prev) => ({ ...prev, ...next }),
            replace: true,
        });
    };

    const resetSearch = () => {
        void navigate({
            to: "/diaries/$diaryId/setup/side",
            params: { diaryId },
            search: {},
            replace: true,
        });
    };

    h.useNav([
        { route: { to: "/diaries" }, label: diaryCopy.diaries },
        {
            route: { to: "/diaries/$diaryId/home/to-do", params: { diaryId } },
            label: diaryCopy.diary,
        },
    ]);

    return (
        <PatientSetupPage title={setupCopy.whichSideAffectedTitle}>
            <div className="space-y-4 text-muted-foreground text-sm">
                <p>
                    {leadIn}
                    <SideSetupHighlightedNames names={names} nameListSeparator={setupCopy.nameListSeparator} />
                    {closing}
                </p>
                <p>{bothSidesNote}</p>
            </div>
            <MissingSideForm
                diaryId={diaryId}
                hideFormIntro
                items={missingSideItems}
                navigateSearch={navigateSearch}
                perItemStepIndex={perItemStepIndex}
                resetSearch={resetSearch}
                footerBackAction={
                    perItemStepIndex == null
                        ? {
                              onClick: () => router.history.back(),
                          }
                        : undefined
                }
                onSuccess={() => {
                    void getPostSetupTarget(diaryId, auth.id).then((target) => navigate(target));
                }}
            />
        </PatientSetupPage>
    );
};

export const Route = createFileRoute("/_app/diaries/$diaryId/setup/side")({
    component: SetupSidePage,
    validateSearch: (search) => sideSetupSearchSchema.parse(search ?? {}),
    loaderDeps: ({ search }) => ({ i: search.i }),
    loader: async ({ context, params, deps }) => {
        const diary = (await context?.queryClient.ensureQueryData(
            diaryByIdQueryOptions(params.diaryId)
        )) as StatefulDiaryT | null;
        if (!diary) {
            throw redirect({ to: "/diaries" });
        }
        const isPatientViewer = (context.auth?.roles?.isPatientOnly ?? false) || context.auth?.id === diary.patient_id;
        if (!isPatientViewer) {
            throw redirect({
                to: "/diaries/$diaryId/home/to-do",
                params: { diaryId: params.diaryId },
            });
        }

        redirectIfConsentRequired(diary, context.auth?.id, isPatientViewer);

        if (patientNeedsHospitalNumberSetup(diary)) {
            throw redirect({
                to: "/diaries/$diaryId/setup/hospital-number",
                params: { diaryId: params.diaryId },
            });
        }
        if (patientNeedsReferenceDateSetup(diary)) {
            throw redirect({
                to: "/diaries/$diaryId/setup/reference-date",
                params: { diaryId: params.diaryId },
            });
        }
        const missingSideItems = buildMissingSideItems(diary);

        if (typeof deps.i === "number") {
            if (missingSideItems.length < 2) {
                throw redirect({
                    to: "/diaries/$diaryId/setup/side",
                    params: { diaryId: params.diaryId },
                    search: {},
                });
            }
            if (deps.i >= missingSideItems.length) {
                throw redirect({
                    to: "/diaries/$diaryId/setup/side",
                    params: { diaryId: params.diaryId },
                    search: {},
                });
            }
        }
        return { diary, missingSideItems };
    },
});
